import React, { useState, useMemo, useEffect } from 'react';
import { toReportNumber, formatReportNumber } from './reportNumbers';
import * as Tooltip from '@radix-ui/react-tooltip';
import KPI from './KPI';
import RankedKPI from './RankedKPI';
import { tiedRankings } from './reportRankings';
import Table from './Table';
import Chart from './Chart';
import { apiFetch } from '../../../../utils/api';


const truncate = (str, max = 20) =>
  String(str || '').length > max
    ? String(str || '').substring(0, max) + '…'
    : String(str || '');

const ColumnInfoHeader = ({ label, explanation }) => (
  <Tooltip.Provider delayDuration={200}>
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <button type="button" className="cursor-help text-left" aria-label={`${label}: ${explanation}`}>
          {label} ⓘ
        </button>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content className="z-[9999] max-w-xs rounded bg-black px-2 py-1 text-xs text-white shadow-lg" sideOffset={5}>
          {explanation}
          <Tooltip.Arrow className="fill-black" />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  </Tooltip.Provider>
);

// Compare Monday–Sunday weeks ending no later than the selected end date or yesterday.
export const getItemComparisonWindow = (endDate, now = new Date()) => {
  const today = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const yesterday = new Date(today.getTime() - 86400000);
  const requested = /^\d{4}-\d{2}-\d{2}$/.test(endDate || '') ? new Date(`${endDate}T00:00:00Z`) : yesterday;
  const anchor = new Date(Math.min(requested.getTime(), yesterday.getTime()));
  anchor.setUTCDate(anchor.getUTCDate() - anchor.getUTCDay());
  const dateAt = offset => new Date(anchor.getTime() + offset * 86400000).toISOString().slice(0, 10);
  return { start: dateAt(-13), previousEnd: dateAt(-7), currentStart: dateAt(-6), end: dateAt(0) };
};

// Compare only the current report's maximum 50 items; missing days count as zero after a successful fetch.
export const buildItemComparisons = (items, days, window) => items.slice(0, 50).map(item => {
  let previous = 0, current = 0;
  days.forEach(day => {
    const date = String(day.date || '').slice(0, 10);
    if (day.name !== item.name || date < window.start || date > window.end) return;
    const quantity = Number(day.total_quantity) || 0;
    if (date < window.currentStart) previous += quantity;
    else current += quantity;
  });
  return { ...item, previous, current, change: current - previous,
    percent: previous > 0 ? (current - previous) / previous * 100 : null };
});

// Share the exact table calculations between on-screen rows and date-specific exports.
export const buildItemReportRows = (data, totalOrders, daily, comparisonWindow) => {
  const comparisons = daily == null ? [] : buildItemComparisons(data, daily, comparisonWindow);
  const changeLabel = item => item.percent == null ? item.current > 0 ? 'New activity' : 'No activity' : `${item.percent > 0 ? '+' : ''}${item.percent.toFixed(1)}%`;
  const comparisonMap = new Map(comparisons.map(item => [item.name, item]));
  return data.slice(0, 50).map(item => ({ ...item,
    total_quantity: toReportNumber(item.total_quantity),
    distinct_orders: toReportNumber(item.distinct_orders),
    order_share: toReportNumber(totalOrders) > 0 && toReportNumber(item.distinct_orders) != null ? Number(item.distinct_orders) / Number(totalOrders) * 100 : null,
    units_per_order: toReportNumber(item.distinct_orders) > 0 && toReportNumber(item.total_quantity) != null ? Number(item.total_quantity) / Number(item.distinct_orders) : null,
    previous: comparisonMap.get(item.name)?.previous ?? null,
    current: comparisonMap.get(item.name)?.current ?? null,
    change: comparisonMap.get(item.name)?.change ?? null,
    change_percent: comparisonMap.has(item.name) ? changeLabel(comparisonMap.get(item.name)) : 'Unavailable',
  }));
};

const ItemsChartsSection = ({ data = [], totalOrders = 0, isDark = false, filters = {} }) => {
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [daily, setDaily] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(true);
  const comparisonWindow = useMemo(
    () => getItemComparisonWindow(filters.endDate),
    [filters.endDate],
  );
  const comparisonQuery = new URLSearchParams(Object.entries({ site_id: filters.site_id, location_id: filters.location_id,
    sublocation_id: filters.sublocation_id, station: filters.station, daypart: filters.daypart,
    startDate: comparisonWindow.start, endDate: comparisonWindow.end }).filter(([, value]) => value != null && value !== '')).toString();
  // Keep the comparison request because the Item Performance table still displays both weekly unit columns.
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setDaily(null);
    setComparisonLoading(true);
    apiFetch(`/updates-reports/items-by-day?${comparisonQuery}`, { signal: controller.signal })
      .then(async response => {
        const raw = await response.text();
        if (!response.ok || /^\s*</.test(raw)) throw new Error('Weekly comparison is unavailable. Check that the updated reports backend is deployed.');
        const rows = JSON.parse(raw);
        if (!Array.isArray(rows)) throw new Error('Unexpected weekly comparison response.');
        if (active) setDaily(rows);
      }).catch(error => {
        if (active) {
          setDaily(null);
          console.warn('Weekly item table values are unavailable.', error);
        }
      }).finally(() => { if (active) setComparisonLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [comparisonQuery, data]);
  const reportRows = buildItemReportRows(data, totalOrders, daily, comparisonWindow);
  const scatter = reportRows.map(item => ({ id: item.name, x: item.total_quantity, y: item.distinct_orders }))
    .filter(item => Number.isFinite(item.x) && Number.isFinite(item.y));

  const handleLimitChange = (e) => {
    const val = parseInt(e.target.value, 10);
    setLimit(val);
  };


  const backendTotal = toReportNumber(data[0]?.total_items_sold);
  const totalItems = Number.isFinite(backendTotal)
    ? backendTotal
    : data.length && data.every(item => toReportNumber(item.total_quantity) != null)
      ? data.reduce((sum, item) => sum + toReportNumber(item.total_quantity), 0) : null;
  const avgItemsPerTicket = totalItems != null && toReportNumber(totalOrders) > 0 ? totalItems / Number(totalOrders) : null;

  const mostOrderedItems = tiedRankings(data, 'total_quantity');
  const leastOrderedItems = tiedRankings(data, 'total_quantity', 'min');
  const minQty = toReportNumber(leastOrderedItems[0]?.total_quantity);

  // Columns for Item Report Table
  const itemColumns = [
    {
      field: 'name',
      headerName: 'Item Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'total_quantity',
      headerName: 'Total Quantity',
      flex: 1,
      minWidth: 150,
      align: 'left',
      headerAlign: 'left',
      renderHeader: () => <ColumnInfoHeader label="Total Quantity" explanation="Total units ordered, including multiple quantities of the same item on one ticket." />,
    },
    {
      field: 'distinct_orders',
      headerName: 'Orders Count',
      flex: 1,
      minWidth: 150,
      align: 'left',
      headerAlign: 'left',
      renderHeader: () => <ColumnInfoHeader label="Orders Count" explanation="Number of unique tickets containing this item, regardless of the quantity on each ticket." />,
    },
    {
      field: 'average_bump_time_seconds',
      headerName: 'Avg. Recorded Elapsed (min)',
      flex: 1,
      minWidth: 160,
      align: 'left',
      headerAlign: 'left',
      renderHeader: () => <ColumnInfoHeader label="Avg. Recorded Elapsed" explanation="Average minutes from the item's recorded creation to its recorded bump. This can include waiting and parked time, so it is not pure cook time or final expo completion." />,

      // Convert seconds → minutes
      valueGetter: (value) => {
        if (toReportNumber(value) == null) {
          return null;
        }
        return Number(value) / 60;
      },

      // What the user actually sees
      renderCell: (params) => {
        return formatReportNumber(params.value, 1, ' min');
      },
    },
    { field: 'order_share', headerName: 'Order Reach', width: 185, type: 'number', renderHeader: () => <ColumnInfoHeader label="Order Reach" explanation="Percentage of matching tickets that included this item. This measures how broadly the item sells, not how many total units were ordered." />, valueFormatter: value => value == null ? '-' : `${Number(value).toFixed(1)}%` },
    { field: 'units_per_order', headerName: 'Units per Order', width: 200, type: 'number', renderHeader: () => <ColumnInfoHeader label="Units per Order" explanation="Average quantity on tickets that contained this item. This measures ordering intensity and excludes tickets where the item was absent." />, valueFormatter: value => value == null ? '-' : Number(value).toFixed(2) },
    { field: 'previous', headerName: 'Previous Week Units', width: 175, type: 'number' },
    { field: 'current', headerName: 'Latest Week Units', width: 165, type: 'number' },
    { field: 'change', headerName: 'Weekly Unit Change', width: 175, type: 'number' },
    { field: 'change_percent', headerName: 'Weekly % Change', width: 170 },
  ];

  return (
    <div className="reports-tab-content dark:text-gray-100 space-y-4">
      {/* KPI Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI
          title="Total Items Ordered"
          value={formatReportNumber(totalItems)}
          color="blue"
        />
        <KPI
          title="Avg. Items Per Ticket"
          value={avgItemsPerTicket == null ? '-' : avgItemsPerTicket.toFixed(1)}
          color="purple" />
        <RankedKPI
          title="Most Ordered Item"
          label={mostOrderedItems.length > 1 ? `${mostOrderedItems.length} items` : mostOrderedItems[0]?.name}
          value={toReportNumber(mostOrderedItems[0]?.total_quantity)}
          showTooltip={mostOrderedItems.length > 1}
          tooltipContent={mostOrderedItems.map((item, index) => <div key={index}>{item.name}</div>)}
          color="green"
        />
        <RankedKPI
          title="Least Ordered Item(s)"
          label={
            leastOrderedItems.length === 1
              ? leastOrderedItems[0].name
              : leastOrderedItems.length > 1
                ? `${leastOrderedItems.length} items`
                : '-'
          }
          value={minQty == null ? '-' : minQty}
          color="yellow"
          showTooltip={leastOrderedItems.length > 1}
          tooltipContent={
            <>
              {leastOrderedItems.map((item, index) => (
                <div key={index}>{item.name}</div>
              ))}
            </>
          }
        />
      </div>
      
      <Chart type="bar" title="Top Items by Quantity" subtitle="Units ordered in the selected report period. Ranking size affects this chart only."
        headerAction={
          <label className="flex flex-wrap items-center justify-end gap-2 text-xs">Ranking size
            <select value={limit} onChange={handleLimitChange} className="max-w-full border rounded-lg px-3 py-2 bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">
              <option value={10}>Top 10</option><option value={20}>Top 20</option><option value={50}>All returned (up to 50)</option>
            </select>
          </label>
        }
        isDark={isDark} layout="horizontal" data={reportRows.slice(0, Math.min(limit, 50))}
        height={Math.max(320, Math.min(data.length, limit, 50) * 34 + 100)}
        xAxis={[{ label: 'Units ordered', min: 0 }]}
        yAxis={[{ dataKey: 'name', scaleType: 'band', width: 180, valueFormatter: name => truncate(name, 26) }]}
        series={[{ dataKey: 'total_quantity', label: 'Units', valueFormatter: (value, context) => {
          const item = reportRows[context?.dataIndex];
          return item ? `${item.name}: ${value} units · ${item.distinct_orders} orders · ${Number(item.distinct_orders) > 0 ? (Number(value) / Number(item.distinct_orders)).toFixed(2) : '-'} units per containing order` : String(value);
        } }]} />
      <Chart type="scatter" title="Quantity versus Order Reach" subtitle="One dot per returned item: farther right means more units; higher means more orders containing the item."
        isDark={isDark} xAxis={[{ label: 'Units ordered', min: 0 }]} yAxis={[{ label: 'Orders containing item', min: 0 }]}
        series={[{ label: 'Item', data: scatter, valueFormatter: point => point ? `${point.id}: ${point.x} units · ${point.y} orders · ${point.y > 0 ? (point.x / point.y).toFixed(2) : '-'} units per containing order` : '' }]} />
      {/* #REVIEW: Top Movers is withheld until complete-week comparisons, zero-history behavior, ranking rules, and client datasets receive additional product validation. Weekly calculations remain active for the Item Performance table. */}
      <Table title="Item Performance" rows={reportRows} columns={itemColumns} isDark={isDark}
        getRowId={row => row.name} height={500} searchable search={search} onSearchChange={setSearch}
        searchPlaceholder="Search by Item..." exportable exportTitle="Item Performance"
        exportFileName="Item Performance" exportDateRange={filters} exportDisabled={comparisonLoading}
        exportHelpText={`Previous week: ${comparisonWindow.start} to ${comparisonWindow.previousEnd}; latest week: ${comparisonWindow.currentStart} to ${comparisonWindow.end}.${daily == null ? ' Weekly values are unavailable.' : ''}`} />
    </div>
  );
};

export default ItemsChartsSection;
